import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../types';
import GCPService from '../services/gcp';

interface AuthContextType {
  user: User | null;
  login: () => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  gcpAuthenticated: boolean;
  gcpLogin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children, initialUser, userTier }: { children: React.ReactNode, initialUser?: Record<string, unknown>, userTier?: 'guest' | 'authenticated' | 'builder' | 'innovator' | 'admin' }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [gcpAuthenticated, setGcpAuthenticated] = useState(false);

  useEffect(() => {
    // If initialUser provided from Codey, use it
    if (initialUser) {
      const newUser: User = {
        id: (initialUser.sub as string) || 'user_' + Date.now(),
        name: (initialUser.name as string) || 'Authenticated User',
        email: (initialUser.email as string) || 'user@gmail.com',
        avatar: 'https://github.com/shadcn.png',
        tier: userTier || 'guest',
        provider: initialUser.provider as string | undefined
      };
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUser(newUser);
      setGcpAuthenticated(true);
      setIsLoading(false);
      return;
    }
    
    // Check for existing session
    const storedUser = localStorage.getItem('acs_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    // Check for GCP authentication
    if (GCPService.isAuthenticated()) {
      setGcpAuthenticated(true);
    }
    
    setIsLoading(false);
  }, [initialUser, userTier]);

  const login = async () => {
    // Google OAuth Flow - In production, uses google-auth-library
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Initialize GCP auth when user logs in
    await GCPService.initializeAuth();
    setGcpAuthenticated(true);
    
    const mockUser: User = {
      id: localStorage.getItem('gcp_user_id') || 'user_123',
      name: localStorage.getItem('gcp_user_name') || 'Demo User',
      email: localStorage.getItem('gcp_user_email') || 'demo@example.com',
      avatar: 'https://github.com/shadcn.png'
    };
    
    setUser(mockUser);
    localStorage.setItem('acs_user', JSON.stringify(mockUser));
    setIsLoading(false);
  };

  const gcpLogin = async () => {
    // Explicit GCP login
    setIsLoading(true);
    try {
      await GCPService.initializeAuth();
      setGcpAuthenticated(true);
      
      // If no general user, create one from GCP auth
      if (!user) {
        const gcpUser = GCPService.getUserInfo();
        if (gcpUser) {
          const newUser: User = {
            id: gcpUser.id,
            name: gcpUser.name || 'Authenticated User',
            email: gcpUser.email || 'user@gmail.com',
            avatar: 'https://github.com/shadcn.png'
          };
          setUser(newUser);
          localStorage.setItem('acs_user', JSON.stringify(newUser));
        }
      }
    } catch (error) {
      console.error('GCP authentication failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setGcpAuthenticated(false);
    localStorage.removeItem('acs_user');
    GCPService.logout();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading, gcpAuthenticated, gcpLogin }}>
      {children}
    </AuthContext.Provider>
  );
}


// Default value used when Creator3D is mounted without an AuthProvider
// (which is the normal path in this project — auth here is purely cosmetic
// for the disabled chat panel). Returning a stable guest avoids crashes.
const GUEST_AUTH: AuthContextType = {
  user: null,
  login: async () => {},
  logout: () => {},
  isLoading: false,
  gcpAuthenticated: false,
  gcpLogin: async () => {},
};

export function useAuth() {
  const context = useContext(AuthContext);
  return context ?? GUEST_AUTH;
}
